/**
 * ReportSnapshotPages — generates one or more A4 report pages for snapshot captures.
 *
 * Each page has its own grid layout (1x1, 1x2, 2x1, 2x2) selectable via a
 * dropdown. The layout determines how many snapshots fit on the page and the
 * cell size (image + label + description) is computed from the page content
 * area divided by rows and columns.
 */

import { Eye } from 'lucide-react';
import type { ReactElement } from 'react';

import { ReportPage } from '../report-page';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getSnapshotDisplayUrl } from '@/visualization/services/viewport-snapshot-service';
import type { ViewportSnapshot } from '@/visualization/services/viewport-snapshot-service';

// ============================================================================
// Layout constants (px, matching ReportPage A4 sizing at 96dpi)
// ============================================================================

/** Usable content height inside a ReportPage (1123px - 80px padding - ~30px footer). */
const PAGE_CONTENT_HEIGHT = 1013;
/** Usable content width inside a ReportPage (794px - 96px horizontal padding). */
const PAGE_CONTENT_WIDTH = 698;
/** Height reserved for the page header (title + layout dropdown). */
const PAGE_HEADER_HEIGHT = 40;
/** Gap between grid rows/columns (Tailwind gap-4 = 16px). */
const GRID_GAP = 16;
/** Height reserved for the label line inside a cell. */
const CELL_LABEL_HEIGHT = 14;
/** Height reserved for the description (line-clamp-2) inside a cell. */
const CELL_DESCRIPTION_HEIGHT = 26;
/** Vertical spacing between label and description. */
const CELL_INNER_GAP = 2;
/** Fixed distance between the image bottom and the caption block. Constant across layouts. */
const CAPTION_OFFSET = 8;

/** Absolute inset (px) for the always-visible toggle button inside a cell. */
function iconInsetFor(layout: GridLayout): number {
    switch (layout) {
        case '1x1':
            return 12;
        case '1x2':
        case '2x1':
            return 10;
        case '2x2':
            return 6;
    }
}

// ============================================================================
// Grid layout
// ============================================================================

export type GridLayout = '1x1' | '1x2' | '2x1' | '2x2';

const LAYOUT_CONFIG: Record<GridLayout, { rows: number; cols: number; label: string }> = {
    '1x1': { rows: 1, cols: 1, label: '1 × 1' },
    '1x2': { rows: 1, cols: 2, label: '1 × 2' },
    '2x1': { rows: 2, cols: 1, label: '2 × 1' },
    '2x2': { rows: 2, cols: 2, label: '2 × 2' },
};

export const DEFAULT_GRID_LAYOUT: GridLayout = '2x2';

function snapshotsPerPageFor(layout: GridLayout): number {
    const { rows, cols } = LAYOUT_CONFIG[layout];
    return rows * cols;
}

/**
 * Compute the ordered list of layouts used to paginate `totalSnapshots`.
 * Layouts provided by the caller are honored in order; any remaining pages
 * fall back to `defaultLayout`.
 */
function computePageLayouts(
    totalSnapshots: number,
    layouts: GridLayout[],
    defaultLayout: GridLayout = DEFAULT_GRID_LAYOUT
): GridLayout[] {
    if (totalSnapshots <= 0) return [layouts[0] ?? defaultLayout];

    const result: GridLayout[] = [];
    let remaining = totalSnapshots;
    let i = 0;
    while (remaining > 0) {
        const layout = layouts[i] ?? defaultLayout;
        result.push(layout);
        remaining -= snapshotsPerPageFor(layout);
        i++;
    }
    return result;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Compute how many snapshot pages are needed for the given counts and layouts.
 * Returns at least 1 (even if there are no snapshots, we still show the page).
 */
export function computeSnapshotPageCount(
    templateCount: number,
    userCount: number,
    layouts: GridLayout[] = [],
    _options?: { hasError?: boolean; isCapturing?: boolean }
): number {
    return computePageLayouts(templateCount + userCount, layouts).length;
}

// ============================================================================
// Public component
// ============================================================================

interface Props {
    /** Page number of the first snapshot page. */
    startPageNumber: number;
    totalPages: number;
    /** Ordered template captures, produced by the capture pass. */
    templateSnapshots: ViewportSnapshot[];
    /** Manual captures taken by the user via the snapshot panel (already filtered). */
    userSnapshots: ViewportSnapshot[];
    isCapturing: boolean;
    error: Error | null;
    /** Toggle a snapshot's report inclusion (hides it from the report and PDF). */
    onToggleReportInclusion: (id: string) => void;
    /** Layout chosen for each snapshot page, in order. Missing entries use the default. */
    layouts: GridLayout[];
    /** Called when the user picks a new layout for a given snapshot page. */
    onChangeLayout: (pageIndex: number, layout: GridLayout) => void;
}

/**
 * Build an array of ReportPage elements for snapshot captures.
 *
 * Returns a plain JSX.Element[] (not a component) so the caller can spread
 * it directly into ReportPreviewLayout's children, giving each page its own
 * scroll-snap wrapper.
 */
export function buildSnapshotPages({
    startPageNumber,
    totalPages,
    templateSnapshots,
    userSnapshots,
    isCapturing,
    error,
    onToggleReportInclusion,
    layouts,
    onChangeLayout,
}: Props): ReactElement[] {
    const hasTemplateSnapshots = templateSnapshots.length > 0;

    const allSnapshots = [...templateSnapshots, ...userSnapshots];
    const pageLayouts = computePageLayouts(allSnapshots.length, layouts);

    // Chunk snapshots according to each page's layout
    const chunks: ViewportSnapshot[][] = [];
    let offset = 0;
    for (const layout of pageLayouts) {
        const count = snapshotsPerPageFor(layout);
        chunks.push(allSnapshots.slice(offset, offset + count));
        offset += count;
    }

    return chunks.map((pageSnapshots, pageIndex) => {
        const layout = pageLayouts[pageIndex];
        const isFirstPage = pageIndex === 0;
        const pageNumber = startPageNumber + pageIndex;

        return (
            <ReportPage key={`snap-${pageIndex}`} pageNumber={pageNumber} totalPages={totalPages}>
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-[11px] font-semibold uppercase tracking-wide text-gray-600">
                        Captures
                    </h2>
                    <LayoutSelector value={layout} onChange={(next) => onChangeLayout(pageIndex, next)} />
                </div>

                {isFirstPage && error && (
                    <p className="mb-3 text-[10px] text-red-600">Erreur&nbsp;: {error.message}</p>
                )}

                {isFirstPage && isCapturing && (
                    <SnapshotSkeleton layout={layout} count={snapshotsPerPageFor(layout)} />
                )}

                {isFirstPage &&
                    !isCapturing &&
                    !hasTemplateSnapshots &&
                    userSnapshots.length === 0 &&
                    !error && (
                        <p className="mb-4 text-center text-[10px] text-gray-400" data-no-pdf>
                            Les captures seront g&eacute;n&eacute;r&eacute;es automatiquement une fois les
                            donn&eacute;es pr&ecirc;tes.
                        </p>
                    )}

                {!isCapturing && pageSnapshots.length > 0 && (
                    <SnapshotGrid
                        snapshots={pageSnapshots}
                        layout={layout}
                        onToggleReportInclusion={onToggleReportInclusion}
                    />
                )}
            </ReportPage>
        );
    });
}

// ============================================================================
// Sub-components
// ============================================================================

function computeCellSize(layout: GridLayout) {
    const { rows, cols } = LAYOUT_CONFIG[layout];
    const availableHeight = PAGE_CONTENT_HEIGHT - PAGE_HEADER_HEIGHT;
    const availableWidth = PAGE_CONTENT_WIDTH;
    const cellHeight = (availableHeight - (rows - 1) * GRID_GAP) / rows;
    const cellWidth = (availableWidth - (cols - 1) * GRID_GAP) / cols;
    const captionBlockHeight = CELL_LABEL_HEIGHT + CELL_DESCRIPTION_HEIGHT + CELL_INNER_GAP;
    const imageMaxHeight = cellHeight - CAPTION_OFFSET - captionBlockHeight;
    return {
        rows,
        cols,
        cellHeight,
        cellWidth,
        imageMaxHeight,
        captionBlockHeight,
        availableHeight,
    };
}

function SnapshotGrid({
    snapshots,
    layout,
    onToggleReportInclusion,
}: {
    snapshots: ViewportSnapshot[];
    layout: GridLayout;
    onToggleReportInclusion: (id: string) => void;
}) {
    const { rows, cols, cellHeight, imageMaxHeight, availableHeight } = computeCellSize(layout);
    const iconInset = iconInsetFor(layout);

    return (
        <div
            className="grid gap-4"
            style={{
                gridTemplateColumns: `repeat(${cols}, 1fr)`,
                gridTemplateRows: `repeat(${rows}, ${cellHeight}px)`,
                height: availableHeight,
            }}
        >
            {snapshots.map((snap) => (
                <figure key={snap.id} className="flex flex-col items-center">
                    <div className="relative">
                        <img
                            src={getSnapshotDisplayUrl(snap)}
                            alt={snap.label ?? snap.viewMode}
                            className="block rounded"
                            style={{
                                maxWidth: '100%',
                                maxHeight: imageMaxHeight,
                                width: 'auto',
                                height: 'auto',
                            }}
                        />
                        <button
                            data-no-pdf
                            className="absolute rounded-sm bg-white/60 p-0.5 backdrop-blur-sm hover:bg-white/80"
                            style={{ right: iconInset, top: iconInset }}
                            onClick={() => onToggleReportInclusion(snap.id)}
                            aria-label="Exclure du rapport"
                        >
                            <Eye className="h-3.5 w-3.5 text-gray-700" />
                        </button>
                    </div>
                    <figcaption className="w-full shrink-0 text-center" style={{ marginTop: CAPTION_OFFSET }}>
                        <span
                            className="block truncate text-[10px] text-gray-500"
                            style={{ height: CELL_LABEL_HEIGHT }}
                        >
                            {snap.label ?? snap.viewMode}
                        </span>
                        <span
                            className="block text-[9px] text-gray-400 line-clamp-2"
                            style={{ height: CELL_DESCRIPTION_HEIGHT, marginTop: CELL_INNER_GAP }}
                        >
                            {snap.description ?? ''}
                        </span>
                    </figcaption>
                </figure>
            ))}
        </div>
    );
}

function SnapshotSkeleton({ layout, count }: { layout: GridLayout; count: number }) {
    const { rows, cols, cellHeight, imageMaxHeight, availableHeight } = computeCellSize(layout);

    return (
        <div
            className="grid gap-4"
            style={{
                gridTemplateColumns: `repeat(${cols}, 1fr)`,
                gridTemplateRows: `repeat(${rows}, ${cellHeight}px)`,
                height: availableHeight,
            }}
        >
            {Array.from({ length: count }).map((_, i) => (
                <div key={i} className="flex flex-col">
                    <div
                        className="w-full rounded bg-gray-200 animate-pulse"
                        style={{ height: imageMaxHeight }}
                    />
                    <div
                        className="mx-auto h-2 w-20 rounded bg-gray-200 animate-pulse"
                        style={{ marginTop: CAPTION_OFFSET }}
                    />
                </div>
            ))}
        </div>
    );
}

function LayoutSelector({ value, onChange }: { value: GridLayout; onChange: (layout: GridLayout) => void }) {
    return (
        <Select value={value} onValueChange={(v) => onChange(v as GridLayout)}>
            <SelectTrigger
                data-no-pdf
                aria-label="Grid layout"
                className="h-7 w-[90px] px-2 py-0 text-[10px]"
            >
                <SelectValue />
            </SelectTrigger>
            <SelectContent>
                {(Object.keys(LAYOUT_CONFIG) as GridLayout[]).map((key) => (
                    <SelectItem key={key} value={key} className="text-[10px]">
                        {LAYOUT_CONFIG[key].label}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}
